import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiFetch } from "@/lib/api";

interface LoginProps {
  onLogin: (user: any) => void;
}

export default function AdminLogin({ onLogin }: LoginProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSetup, setIsSetup] = useState(false);
  const [hasOwner, setHasOwner] = useState(true);

  useEffect(() => {
    apiFetch("/users/has-owner")
      .then((data) => setHasOwner(data.hasOwner))
      .catch(() => setHasOwner(true));
  }, []);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await apiFetch("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      onLogin(user);
    } catch (err: any) {
      if (err.message?.includes("Invalid")) {
        setError("Invalid email or password");
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSetup(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await apiFetch("/users/setup", {
        method: "POST",
        body: JSON.stringify({ email, password, name }),
      });
      onLogin(user);
    } catch (err: any) {
      if (err.message?.includes("Setup already completed")) {
        setIsSetup(false);
        setError("An account already exists. Please log in.");
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="w-full max-w-md p-8 bg-white shadow-lg" style={{ borderRadius: "16px 0 16px 0" }}>
        <div className="text-center mb-8">
          <h1 className="text-2xl font-serif font-bold text-primary">Tallahassee Lassie</h1>
          <p className="text-muted-foreground mt-1">
            {isSetup ? "Create your owner account" : "Admin Login"}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-md">
            {error}
          </div>
        )}

        {isSetup ? (
          <form onSubmit={handleSetup} className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
            </div>
            <Button type="submit" className="w-full" disabled={loading} style={{ borderRadius: "16px 0 16px 0" }}>
              {loading ? "Creating..." : "Create Owner Account"}
            </Button>
            <button type="button" onClick={() => setIsSetup(false)} className="text-sm text-primary underline w-full text-center block mt-2">
              Already have an account? Login
            </button>
          </form>
        ) : (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <Button type="submit" className="w-full" disabled={loading} style={{ borderRadius: "16px 0 16px 0" }}>
              {loading ? "Logging in..." : "Login"}
            </Button>
            {!hasOwner && (
              <button type="button" onClick={() => setIsSetup(true)} className="text-sm text-primary underline w-full text-center block mt-2">
                First time? Set up owner account
              </button>
            )}
            {hasOwner && (
              <p className="text-xs text-muted-foreground text-center mt-3">
                Forgot your password? Email{" "}
                <a href="mailto:tallahasseelassie@gmail.com" className="text-primary underline">
                  tallahasseelassie@gmail.com
                </a>{" "}
                to request a reset.
              </p>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
